'use client'

import {
  LazyMotion,
  Variants,
  domAnimation,
  m,
  useReducedMotion,
} from 'framer-motion'

import { Cote } from '@/ui/components/marks'
import { Action, PhoneAction } from '@/ui/components/ui/action'

/**
 * Chiffres du bandeau bas. Tous repris mot pour mot d'ailleurs sur le
 * site (valeurs, FAQ, prestations) — aucune donnée inventée ici.
 */
const RELEVE = [
  { value: '20 ans', label: "d'expérience" },
  { value: '78790', label: 'Septeuil' },
  { value: '7j/7', label: 'serrurier' },
  { value: '30–60 min', label: 'selon la zone' },
]

export default function CTA() {
  const shouldReduceMotion = useReducedMotion()

  // Une seule séquence orchestrée au chargement, plutôt que des effets
  // dispersés section par section.
  const stage: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: shouldReduceMotion ? 0 : 0.1,
      },
    },
  }

  const rise: Variants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  }

  return (
    <section className="on-dark relative flex min-h-[calc(100svh-5rem)] desktop:min-h-[calc(100svh-6rem)] items-end">
      {/* Trame millimétrée : la page se lit comme une feuille de relevé */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 grid-paper text-chalk opacity-40"
      />

      <div className="shell relative w-full pb-14 pt-24 tablet:pb-20 desktop:pb-24">
        <LazyMotion features={domAnimation}>
          <m.div variants={stage} initial="hidden" animate="visible">
            <m.h1
              variants={rise}
              className="flex flex-col text-chalk max-w-[18ch]"
            >
              <span className="mb-6 font-mono text-xs tracking-[0.3em] font-medium text-mist">
                NICO&nbsp;BAT
              </span>
              Travaux tous corps d&apos;état dans les&nbsp;Yvelines
            </m.h1>

            <m.div variants={rise} className="mt-10 max-w-[62ch]">
              <p className="text-chalk/85 text-lg tablet:text-xl leading-relaxed">
                Rénovation complète,{' '}
                <strong className="font-semibold text-chalk">plomberie</strong>,{' '}
                <strong className="font-semibold text-chalk">
                  électricité
                </strong>
                ,{' '}
                <strong className="font-semibold text-chalk">menuiserie</strong>
                , <strong className="font-semibold text-chalk">vitrerie</strong>{' '}
                et{' '}
                <strong className="font-semibold text-chalk">serrurerie</strong>.
              </p>
              <p className="mt-3 font-mono text-sm tracking-[0.04em] text-mist">
                Devis sous 24h, interventions rapides, finitions soignées.
              </p>
            </m.div>

            <m.div
              variants={rise}
              className="mt-12 flex flex-col tablet:flex-row tablet:flex-wrap tablet:items-end gap-8 tablet:gap-x-14 tablet:gap-y-8"
            >
              <Action href="#contact" variant="solid-light">
                Contactez-nous
              </Action>
              <PhoneAction tone="light" label="Appelez-nous directement" />
            </m.div>

            {/* Le relevé : cotes portant des données réelles du site */}
            <m.dl
              variants={rise}
              className="mt-16 tablet:mt-20 grid grid-cols-2 desktop:grid-cols-4 gap-x-8 gap-y-8 border-t border-chalk/15 pt-8"
            >
              {RELEVE.map((item) => (
                <div key={item.value} className="flex flex-col gap-1.5">
                  <dt className="sr-only">{item.label}</dt>
                  <dd
                    data-figure
                    className="font-display text-2xl tablet:text-3xl font-bold text-chalk tracking-tight leading-none"
                  >
                    {item.value}
                  </dd>
                  <span className="annot text-mist" aria-hidden="true">
                    {item.label}
                  </span>
                </div>
              ))}
            </m.dl>
          </m.div>
        </LazyMotion>
      </div>
    </section>
  )
}

/**
 * Conservé pour compatibilité : d'autres surfaces importent encore
 * CtaPill. Redirigé vers le nouveau bouton.
 */
export function CtaPill({
  href = '#contact',
  label = 'Contactez-nous',
}: {
  href?: string
  label?: string
}) {
  return (
    <Action href={href} variant="ghost" aria-label={label}>
      {label}
    </Action>
  )
}

export { Cote }
